import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
const _excluded = ["autoFocus", "children", "className", "component", "componentProps", "componentRef", "defaultValue", "disabled", "FormHelperTextProps", "fullWidth", "helperText", "helperTextVisible", "id", "InputLabelProps", "intent", "label", "name", "onBlur", "onChange", "onFocus", "placeholder", "required", "value"];
import React, { forwardRef } from 'react';
import PropTypes, { elementType, oneOf, oneOfType, shape, any } from 'prop-types';
import warning from 'warning';
import classNames from 'classnames';
import FormControl from '../form-control';
import FormHelperText from '../form-helper-text/FormHelperText';
import InputLabel from '../input-label/InputLabel';
import Input from '../input/Input';
import Select from '../select/Select';
import CurrencyInput from '../currency-input/CurrencyInput';
import DateInput from '../date-input/DateInput';
import PhoneNumberInput from '../phone-number-input/PhoneNumberInput';
import SSNInput from '../ssn-input/SSNInput';
import MaskedInput from '../masked-input/MaskedInput';
import useTextHelperStyles from './TextField.styles';
const componentMap = {
  Input,
  Select,
  Currency: CurrencyInput,
  Date: DateInput,
  Phone: PhoneNumberInput,
  SSN: SSNInput,
  CustomMask: MaskedInput
};
const helperTextMap = {
  Date: 'MM/DD/YY',
  Phone: '(###) ###-####',
  SSN: '###-##-####'
};
const TextField = /*#__PURE__*/forwardRef(function TextField(props, ref) {
  const {
      autoFocus,
      children,
      className,
      component,
      componentProps = {},
      componentRef,
      defaultValue,
      disabled,
      FormHelperTextProps = {},
      fullWidth,
      helperText = helperTextMap[props.component],
      helperTextVisible,
      id,
      InputLabelProps = {},
      intent,
      label,
      name,
      onBlur,
      onChange,
      onFocus,
      placeholder,
      required,
      value
    } = props,
    otherProps = _objectWithoutProperties(props, _excluded);
  const classes = useTextHelperStyles();
  const textHelperAlwaysVisible = helperTextVisible ? classes.root : undefined;
  const helperTextId = helperText && id ? `${id}-helper-text` : undefined;
  const inputLabelId = label && id ? `${id}-label` : undefined;
  const isSelect = component === 'Select';
  warning(!isSelect || isSelect && children, 'PDS::TextField: `children` must be passed when using the `TextField` component with `Select`.');

  // For the select and if a match is not found we always want our Input
  let InputComponent = Input;
  if (!isSelect && componentMap[component]) {
    InputComponent = componentMap[component];
  } else if (!isSelect) {
    InputComponent = component;
  }
  const InputElement = /*#__PURE__*/React.createElement(InputComponent, Object.assign({
    "aria-describedby": helperTextId,
    autoFocus: autoFocus,
    defaultValue: defaultValue,
    id: id,
    inputRef: componentRef,
    intent: intent,
    name: name,
    onBlur: onBlur,
    onChange: onChange,
    onFocus: onFocus,
    placeholder: placeholder,
    value: value
  }, otherProps, componentProps));
  return (
    /*#__PURE__*/
    // do not pass the required flag to the FormControl or it will add a * instead
    React.createElement(FormControl, {
      className: classNames('pds-text-field', className),
      disabled: disabled,
      fullWidth: fullWidth,
      intent: intent,
      ref: ref
    }, label && /*#__PURE__*/React.createElement(InputLabel, Object.assign({
      htmlFor: id,
      id: inputLabelId,
      required: required
    }, InputLabelProps), label), isSelect ? /*#__PURE__*/React.createElement(Select, Object.assign({
      "aria-describedby": helperTextId,
      autoFocus: autoFocus,
      defaultValue: defaultValue,
      id: id,
      input: InputElement,
      inputRef: componentRef,
      intent: intent,
      labelId: inputLabelId,
      name: name,
      onBlur: onBlur,
      onChange: onChange,
      onFocus: onFocus,
      value: value
    }, otherProps, componentProps), children) : InputElement, helperText && /*#__PURE__*/React.createElement(FormHelperText, Object.assign({
      id: helperTextId
    }, FormHelperTextProps, {
      className: classNames(FormHelperTextProps.className, textHelperAlwaysVisible, {
        'Mui-warning': intent === 'warning'
      })
    }), helperText))
  );
});
TextField.defaultProps = {
  component: 'Input'
};
TextField.propTypes = {
  /**
   * This prop helps users to fill forms faster, especially on mobile devices.
   * The name can be confusing, as it's more like an autofill.
   * You can learn more about it [following the specification](https://html.spec.whatwg.org/multipage/form-control-infrastructure.html#autofill).
   */
  autoComplete: PropTypes.string,
  /**
   * If `true`, the `input` element will be focused during the first mount.
   */
  autoFocus: PropTypes.bool,
  /**
   * If `select` prop is true then this should be the option elements to populate the select with.
   */
  children: PropTypes.node,
  /**
   * The CSS class name applied to the root FormControl component.
   */
  className: PropTypes.string,
  /**
   * The component to use inside the TextField, by default an Input is used.
   */
  component: oneOfType([oneOf(['Input', 'Select', 'Currency', 'Date', 'Phone', 'SSN']), elementType]),
  /**
   * Additional props that will be set in the Input or Select that will be rendered.
   *
   * Also any other prop that is not listed in this documentation will be forwarded
   * to the component.
   */
  componentProps: PropTypes.object,
  /**
   * Pass a ref to the `input` element.
   */
  componentRef: PropTypes.object,
  /**
   * The default value of the `input` element.
   */
  defaultValue: PropTypes.any,
  /**
   * If `true`, the `input` element will be disabled.
   */
  disabled: PropTypes.bool,
  /**
   * Props applied to the [`FormHelperText`](/api/form-helper-text/) element.
   */
  FormHelperTextProps: PropTypes.object,
  /**
   * If `true`, the input will take up the full width of its container.
   */
  fullWidth: PropTypes.bool,
  /**
   * The helper text content.
   */
  helperText: PropTypes.node,
  /**
   * Set helper text visible all the time
   */
  helperTextVisible: PropTypes.bool,
  /**
   * The id of the `input` element.
   * Use this prop to make `label` and `helperText` accessible for screen readers.
   */
  id: PropTypes.string,
  /**
   * Props applied to the [`InputLabel`](/api/input-label/) element.
   */
  InputLabelProps: PropTypes.object,
  /**
   * If it should have the `error`, `warning` or default styles.
   */
  intent: PropTypes.oneOf([undefined, 'warning', 'error']),
  /**
   * The label content.
   */
  label: PropTypes.node,
  /**
   * Name attribute of the `input` element.
   */
  name: PropTypes.string,
  /**
   * @ignore
   */
  onBlur: PropTypes.func,
  /**
   * Callback fired when the value is changed.
   *
   * @param {object} event The event source of the callback.
   * You can pull out the new value by accessing `event.target.value` (string).
   */
  onChange: PropTypes.func,
  /**
   * @ignore
   */
  onFocus: PropTypes.func,
  /**
   * The short hint displayed in the input before the user enters a value.
   */
  placeholder: PropTypes.string,
  /**
   * If `true`, the label is displayed as required and the `input` element` will be required.
   */
  required: PropTypes.bool,
  /**
   * The value of the `input` element, required for a controlled component.
   */
  value: PropTypes.any,
  /**
   * Refer to [IMask documentation](https://imask.js.org/guide.html#masked).
   * Applies just for Custom Mask component
   */
  maskOptions: shape({
    mask: any.isRequired
  })
};
export default TextField;